import { productsCol, productRef } from '@/backend/firebase/services/helpers/firestore-refs';
import { getDocs, query, orderBy, addDoc, updateDoc, where, serverTimestamp, getDoc } from 'firebase/firestore';
import type { Product, ProductWithId, ProductInput } from '@/shared/schema';

export const ProductService = {
  /**
   * Obtiene todos los productos del negocio
   */
  getProducts: async (ownerId: string): Promise<ProductWithId[]> => {
    try {
      const q = query(
        productsCol(ownerId),
        orderBy('audit.updatedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as ProductWithId[];
      
      // Filtrar localmente para capturar tanto los que tienen deletedAt: null 
      // como los que NO tienen el campo deletedAt en lo absoluto en Firestore.
      return products.filter(p => !p.audit?.deletedAt);
    } catch (error) {
      console.error(`[ProductService:getProducts] ❌ Error fetching products:`, error);
      return [];
    }
  },

  /**
   * Crea un nuevo producto
   */
  create: async (ownerId: string, data: ProductInput): Promise<string> => {
    const docRef = await addDoc(productsCol(ownerId), {
      ...data,
      ownerId,
      audit: {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deletedAt: null,
        createdBy: ownerId,
      }
    } as any);
    return docRef.id;
  },

  /**
   * Actualiza un producto existente
   */
  update: async (ownerId: string, productId: string, data: Partial<Product>): Promise<void> => {
    const docRef = productRef(ownerId, productId);
    await updateDoc(docRef, {
      ...data,
      'audit.updatedAt': serverTimestamp(),
      'audit.updatedBy': ownerId,
    });
  },

  /**
   * Obtiene un producto individual por su ID
   */
  getById: async (ownerId: string, productId: string): Promise<ProductWithId | null> => {
    const docRef = productRef(ownerId, productId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;

    const data = snap.data();
    // Seguridad: Si está marcado como borrado, para el frontend no existe
    if (data.audit?.deletedAt) return null;

    return { ...data, id: snap.id } as ProductWithId;
  },

  /**
   * Realiza un borrado lógico (Soft Delete).
   * El producto deja de aparecer en las listas pero permanece en la DB.
   */
  delete: async (ownerId: string, productId: string): Promise<void> => {
    const docRef = productRef(ownerId, productId);
    await updateDoc(docRef, {
      'audit.deletedAt': serverTimestamp(),
      'audit.updatedAt': serverTimestamp(),
      'audit.updatedBy': ownerId,
    });
  },

  /**
   * Obtiene todos los productos eliminados
   */
  getDeleted: async (ownerId: string): Promise<ProductWithId[]> => {
    const q = query(
      productsCol(ownerId),
      where('audit.deletedAt', '!=', null),
      orderBy('audit.deletedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    })) as ProductWithId[];
  },

  /**
   * Restaura un producto eliminado
   */
  restore: async (ownerId: string, productId: string): Promise<void> => {
    const docRef = productRef(ownerId, productId);
    await updateDoc(docRef, {
      'audit.deletedAt': null,
      'audit.updatedAt': serverTimestamp(),
      'audit.updatedBy': ownerId,
    });
  }
};
